import { Menu } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { TeambinderAiApiService } from '@src/api';
import { ItemType } from 'antd/lib/menu/hooks/useItems';

interface DynamicDropdownMenuProps {
  keywordId: string;
  keyword: string;
  index: number;
  onRemove: (index: number) => void;
  onKeywordDefinition: (keyword: string, keywordId: string) => void;
  onCompetitorKeywordDefinition?: (keyword: string, keywordId: string) => void;
  currentFolderId?: string | null;
  folderOptions?: {
    folderId: string;
    name: string;
  }[];
  onMoveFolder?: (keywordId: string, folderId: string | null) => void;
}

const DynamicDropdownMenu = ({
  keywordId,
  keyword,
  index,
  onRemove,
  onKeywordDefinition,
  onCompetitorKeywordDefinition,
  currentFolderId = null,
  folderOptions = [],
  onMoveFolder,
}: DynamicDropdownMenuProps) => {
  const [menuItems, setMenuItems] = useState<ItemType[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const getMoveFolderMenuItem = (): ItemType | null => {
      if (
        !onMoveFolder ||
        (folderOptions.length === 0 && currentFolderId === null)
      ) {
        return null;
      }

      return {
        label: '폴더 이동',
        key: 'move-folder',
        children: [
          ...folderOptions.map((folder) => ({
            label: folder.name,
            key: `move-folder-${folder.folderId}`,
            disabled: currentFolderId === folder.folderId,
            onClick: (e: any) => {
              e.domEvent.stopPropagation();
              onMoveFolder(keywordId, folder.folderId);
            },
          })),
          {
            label: '폴더 없음',
            key: 'move-folder-unassigned',
            disabled: currentFolderId === null,
            onClick: (e: any) => {
              e.domEvent.stopPropagation();
              onMoveFolder(keywordId, null);
            },
          },
        ],
      };
    };

    const getBaseItems = (): ItemType[] => [
      {
        label: '삭제',
        key: 'remove',
        icon: null,
        onClick: (e) => {
          e.domEvent.stopPropagation();
          onRemove(index);
        },
      },
      {
        label: '키워드 정의',
        key: 'edit',
        icon: null,
        onClick: (e) => {
          e.domEvent.stopPropagation();
          onKeywordDefinition(keyword, keywordId);
        },
      },
    ];

    const appendMoveFolder = (items: ItemType[]) => {
      const moveFolderMenuItem = getMoveFolderMenuItem();
      if (moveFolderMenuItem) {
        items.push(moveFolderMenuItem);
      }
    };

    const checkCompetitorKeywords = async () => {
      const items = getBaseItems();
      try {
        const response = await TeambinderAiApiService.findCompetitor({
          keywordId,
        });
        const competitorKeywords = response.data ?? [];
        const hasCompetitors = competitorKeywords.length > 0;

        if (hasCompetitors && onCompetitorKeywordDefinition) {
          // 첫 번째 경쟁사 키워드 ID를 사용
          const firstCompetitorId = competitorKeywords[0];
          items.push({
            label: '경쟁사 키워드 정의',
            key: 'competitor-edit',
            icon: null,
            onClick: (e) => {
              e.domEvent.stopPropagation();
              onCompetitorKeywordDefinition(keyword, firstCompetitorId);
            },
          });
        }
      } catch (error) {
        console.error('Error checking competitor keywords:', error);
      } finally {
        appendMoveFolder(items);
        setMenuItems(items);
        setIsLoaded(true);
      }
    };

    checkCompetitorKeywords();
  }, [
    keywordId,
    keyword,
    index,
    onRemove,
    onKeywordDefinition,
    onCompetitorKeywordDefinition,
    currentFolderId,
    folderOptions,
    onMoveFolder,
  ]);

  if (!isLoaded) {
    return (
      <Menu
        items={[
          {
            label: '로딩 중...',
            key: 'loading',
            disabled: true,
          },
        ]}
      />
    );
  }

  return (
    <Menu
      items={menuItems}
      expandIcon={() => (
        <span
          style={{
            position: 'absolute',
            right: 16,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            color: '#9CA3AF',
            fontSize: 10,
            pointerEvents: 'none',
          }}
        >
          <RightOutlined />
        </span>
      )}
    />
  );
};

export default DynamicDropdownMenu;
